import React, { useState, useMemo, useEffect } from 'react';
import type {
  MergeSuggestion,
  MergeSuggestionsResponse,
  MergePreview,
  MergeRequest,
  MergeResponse,
} from '../../services/api/domains/merge/types';
import { modalStyles } from './styles';
import useAuth from '../../auth/auth';
import type { ApiState } from '../../services/api/types';

// Generic configuration for domain-specific behavior
export interface MergeModalConfig<T> {
  domainDisplayName: string; // "Therapeutic Area", "Product Type", "Manufacturer", etc.
  domainDisplayNamePlural: string; // "Therapeutic Areas", "Product Types", "Manufacturers", etc.
  entityIdField: keyof T;
  entityNameField: keyof T;
  entityCreatedDateField: keyof T;
  useMergeSuggestions: (
    sourceId: number | null,
    maxResults?: number
  ) => ApiState<MergeSuggestionsResponse<T>> & {
    refetch: () => Promise<void>;
    invalidate: () => void;
  };
  useMergePreview: (
    sourceId: number | null,
    targetId: number | null
  ) => ApiState<MergePreview> & {
    refetch: () => Promise<void>;
    invalidate: () => void;
  };
  useMerge: () => ApiState<MergeResponse> & {
    mutate: (variables: {
      sourceId: number;
      targetId: number;
      request: MergeRequest;
    }) => Promise<MergeResponse>;
  };
  // Optional render function for domain-specific conflict resolution
  renderConflicts?: (
    previewData: MergePreview,
    resolvedFields: Record<
      string,
      string | number | boolean | Date | null | undefined
    >,
    onFieldResolve: (
      fieldName: string,
      value: string | number | boolean | Date | null | undefined,
      source: 'source' | 'target'
    ) => void
  ) => React.ReactNode;
}

interface GenericMergeModalProps<T> {
  source: T;
  allEntities: T[];
  config: MergeModalConfig<T>;
  onClose: () => void;
  onSuccess: () => void;
}

type MergeStep = 'SELECT_TARGET' | 'RESOLVE_CONFLICTS' | 'CONFIRM';

function GenericMergeModal<T>({
  source: initialSource,
  allEntities,
  config,
  onClose,
  onSuccess,
}: GenericMergeModalProps<T>) {
  const { account } = useAuth();
  const [step, setStep] = useState<MergeStep>('SELECT_TARGET');
  const [source, setSource] = useState<T>(initialSource);
  const [selectedTargetId, setSelectedTargetId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCanonicalName, setSelectedCanonicalName] = useState<
    'source' | 'target'
  >('target');
  const [mergeReason, setMergeReason] = useState('');
  const [showHelp, setShowHelp] = useState(false);
  const [resolvedFields, setResolvedFields] = useState<
    Record<string, string | number | boolean | Date | null | undefined>
  >({});

  const {
    domainDisplayName,
    domainDisplayNamePlural,
    entityIdField,
    entityNameField,
    entityCreatedDateField,
    useMergeSuggestions,
    useMergePreview,
    useMerge,
    renderConflicts,
  } = config;

  // Get source entity properties
  const sourceId = source[entityIdField] as number;
  const sourceName = source[entityNameField] as string;
  const sourceCreatedDate = source[entityCreatedDateField] as string;

  // Fetch merge suggestions
  const suggestionsQuery = useMergeSuggestions(sourceId);

  // Fetch merge preview when target is selected
  const previewQuery = useMergePreview(
    step === 'RESOLVE_CONFLICTS' || step === 'CONFIRM' ? sourceId : null,
    selectedTargetId
  );

  // Merge mutation
  const mergeMutation = useMerge();

  // Extract data safely
  const suggestionsData =
    suggestionsQuery.status === 'success' ? suggestionsQuery.data : undefined;
  const previewData =
    previewQuery.status === 'success' ? previewQuery.data : undefined;

  // Filter entities based on search
  const filteredTargets = useMemo(() => {
    const suggestions = suggestionsData?.suggestions || [];
    const availableTargets = allEntities.filter(
      entity =>
        entity[entityIdField] !== sourceId &&
        String(entity[entityNameField])
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
    );

    // Combine suggestions with search results, removing duplicates
    const suggestionIds = new Set(
      suggestions.map((s: MergeSuggestion) => s.id)
    );
    const nonSuggestionTargets = availableTargets.filter(
      entity => !suggestionIds.has(entity[entityIdField] as number)
    );

    return {
      suggestions: suggestions
        .map((s: MergeSuggestion) => {
          const entity = allEntities.find(e => e[entityIdField] === s.id);
          return entity
            ? { ...entity, similarity_score: s.similarity_score }
            : null;
        })
        .filter(
          (
            e: (T & { similarity_score: number }) | null
          ): e is T & { similarity_score: number } => e !== null
        ),
      others: nonSuggestionTargets,
    };
  }, [
    suggestionsData,
    allEntities,
    sourceId,
    searchTerm,
    entityIdField,
    entityNameField,
  ]);

  const selectedTarget = useMemo(() => {
    if (!selectedTargetId) return null;
    return (
      allEntities.find(entity => entity[entityIdField] === selectedTargetId) ||
      null
    );
  }, [selectedTargetId, allEntities, entityIdField]);

  // Auto-select target name by default
  useEffect(() => {
    if (selectedTarget) {
      setSelectedCanonicalName('target');
    }
  }, [selectedTarget]);

  const handleTargetSelect = (targetId: number) => {
    setSelectedTargetId(targetId);
    setStep('RESOLVE_CONFLICTS');
  };

  const handleBack = () => {
    if (step === 'RESOLVE_CONFLICTS') {
      setStep('SELECT_TARGET');
      setSelectedTargetId(null);
    } else if (step === 'CONFIRM') {
      setStep('RESOLVE_CONFLICTS');
    }
  };

  const handleProceedToConfirm = () => {
    setStep('CONFIRM');
  };

  const handleSwapSourceTarget = () => {
    if (!selectedTargetId || !selectedTarget) return;

    // Swap the source and target entities
    const newSource = selectedTarget;
    const newTargetId = sourceId;

    setSource(newSource);
    setSelectedTargetId(newTargetId);

    // Reset to default selection (target name)
    setSelectedCanonicalName('target');
  };

  const handleFieldResolve = (
    fieldName: string,
    value: string | number | boolean | Date | null | undefined,
    _: 'source' | 'target'
  ) => {
    setResolvedFields(prev => ({
      ...prev,
      [`resolved_${fieldName}`]: value,
    }));
  };

  const handleExecuteMerge = async () => {
    if (!selectedTargetId || !account || !selectedTarget) return;

    const canonicalName =
      selectedCanonicalName === 'source'
        ? sourceName
        : (selectedTarget[entityNameField] as string);

    try {
      await mergeMutation.mutate({
        sourceId: sourceId,
        targetId: selectedTargetId,
        request: {
          new_canonical_name: canonicalName,
          merge_reason: mergeReason || undefined,
          user_id: account.username || account.name || 'Unknown',
          // Include resolved fields for conflict resolution
          ...resolvedFields,
        },
      });

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Merge failed:', error);
      alert(`Merge failed: ${error}`);
    }
  };

  return (
    <div style={modalStyles.modalOverlayStyle} onClick={onClose}>
      <div
        style={{
          ...modalStyles.modalContentStyle,
          width: '800px',
          maxWidth: '90vw',
        }}
        onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={modalStyles.modalHeaderStyle}>
          <h2 style={modalStyles.modalTitleStyle}>
            Merge {domainDisplayName}: {sourceName}
          </h2>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              onClick={() => setShowHelp(!showHelp)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '16px',
                cursor: 'pointer',
                color: '#6b7280',
                padding: '4px 8px',
                borderRadius: '4px',
                transition: 'all 0.2s ease-in-out',
              }}
              onMouseEnter={e =>
                Object.assign(e.currentTarget.style, {
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                })
              }
              onMouseLeave={e =>
                Object.assign(e.currentTarget.style, {
                  backgroundColor: 'transparent',
                  color: '#6b7280',
                })
              }
              title={showHelp ? 'Hide Help' : 'Show Help'}>
              {showHelp ? '✕ Help' : 'ℹ️ Help'}
            </button>
            <button
              onClick={onClose}
              style={modalStyles.modalCloseXStyle}
              onMouseEnter={e =>
                Object.assign(
                  e.currentTarget.style,
                  modalStyles.modalCloseXHoverStyle
                )
              }
              onMouseLeave={e =>
                Object.assign(
                  e.currentTarget.style,
                  modalStyles.modalCloseXStyle
                )
              }>
              ×
            </button>
          </div>
        </div>

        {/* Collapsible Help Section */}
        {showHelp && (
          <div
            style={{
              backgroundColor: '#f0f9ff',
              border: '1px solid #3b82f6',
              borderRadius: '4px',
              padding: '16px',
              marginBottom: '16px',
            }}>
            <h3 style={{ marginTop: 0, fontSize: '14px', fontWeight: '600' }}>
              How to Merge {domainDisplayNamePlural}
            </h3>
            <ol
              style={{
                marginLeft: '20px',
                lineHeight: '1.6',
                fontSize: '14px',
              }}>
              <li>
                <strong>Select Target:</strong> Choose which{' '}
                {domainDisplayName.toLowerCase()} to merge into. Suggested
                similar names are shown first.
              </li>
              <li>
                <strong>Resolve Conflicts:</strong> Choose which name to keep as
                the canonical name. The source {domainDisplayName.toLowerCase()}{' '}
                will be marked inactive.
              </li>
              <li>
                <strong>Preview Changes:</strong> Review how many products will
                be affected and confirm the merge.
              </li>
              <li>
                <strong>Note:</strong> All products linked to the source will be
                automatically updated to reference the target. This action
                cannot be undone.
              </li>
            </ol>
          </div>
        )}

        {/* Modal Body */}
        <div style={modalStyles.modalBodyStyle}>
          {/* Step 1: Select Target */}
          {step === 'SELECT_TARGET' && (
            <>
              <h3 style={{ marginTop: 0 }}>
                Step 1: Select Target {domainDisplayName}
              </h3>
              <p style={{ color: '#6b7280', marginBottom: '16px' }}>
                Choose which {domainDisplayName.toLowerCase()} to merge "
                {sourceName}" into.
              </p>

              {/* Search Input */}
              <input
                type="text"
                placeholder={`Search ${domainDisplayNamePlural.toLowerCase()}...`}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                style={{
                  ...modalStyles.floatingInputStyle,
                  marginBottom: '16px',
                }}
              />

              {/* Suggested Matches */}
              {filteredTargets.suggestions.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  <h4
                    style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#374151',
                    }}>
                    Suggested Matches
                  </h4>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                    }}>
                    {filteredTargets.suggestions.map(entity => (
                      <button
                        key={entity[entityIdField] as number}
                        onClick={() =>
                          handleTargetSelect(entity[entityIdField] as number)
                        }
                        style={{
                          padding: '12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          backgroundColor: '#fef3c7',
                          textAlign: 'left',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          transition: 'all 0.2s ease-in-out',
                        }}
                        onMouseEnter={e =>
                          Object.assign(e.currentTarget.style, {
                            backgroundColor: '#fde68a',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                          })
                        }
                        onMouseLeave={e =>
                          Object.assign(e.currentTarget.style, {
                            backgroundColor: '#fef3c7',
                            boxShadow: 'none',
                          })
                        }>
                        <div>
                          <div style={{ fontWeight: '500' }}>
                            {entity[entityNameField] as string}
                          </div>
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>
                            Similarity: {entity.similarity_score}%
                          </div>
                        </div>
                        <span style={{ color: '#3b82f6', fontSize: '14px' }}>
                          Select →
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* All Other Targets */}
              {filteredTargets.others.length > 0 && (
                <div>
                  <h4
                    style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#374151',
                    }}>
                    All {domainDisplayNamePlural}
                  </h4>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      maxHeight: '300px',
                      overflow: 'auto',
                    }}>
                    {filteredTargets.others.map(entity => (
                      <button
                        key={entity[entityIdField] as number}
                        onClick={() =>
                          handleTargetSelect(entity[entityIdField] as number)
                        }
                        style={{
                          padding: '12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          backgroundColor: '#ffffff',
                          textAlign: 'left',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          transition: 'all 0.2s ease-in-out',
                        }}
                        onMouseEnter={e =>
                          Object.assign(e.currentTarget.style, {
                            backgroundColor: '#f3f4f6',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                          })
                        }
                        onMouseLeave={e =>
                          Object.assign(e.currentTarget.style, {
                            backgroundColor: '#ffffff',
                            boxShadow: 'none',
                          })
                        }>
                        <div style={{ fontWeight: '500' }}>
                          {entity[entityNameField] as string}
                        </div>
                        <span style={{ color: '#3b82f6', fontSize: '14px' }}>
                          Select →
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {filteredTargets.suggestions.length === 0 &&
                filteredTargets.others.length === 0 && (
                  <p
                    style={{
                      color: '#9ca3af',
                      textAlign: 'center',
                      padding: '32px',
                    }}>
                    No {domainDisplayNamePlural.toLowerCase()} found. Try a
                    different search term.
                  </p>
                )}
            </>
          )}

          {/* Step 2: Resolve Conflicts */}
          {step === 'RESOLVE_CONFLICTS' && selectedTarget && (
            <>
              <h3 style={{ marginTop: 0 }}>Step 2: Resolve Conflicts</h3>
              <p style={{ color: '#6b7280', marginBottom: '16px' }}>
                Choose which {domainDisplayName.toLowerCase()} name to keep. The
                source will be merged into the target (marked inactive).
              </p>

              {/* Swap Button */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginBottom: '16px',
                }}>
                <button
                  onClick={handleSwapSourceTarget}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#3b82f6',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease-in-out',
                  }}
                  onMouseEnter={e =>
                    Object.assign(e.currentTarget.style, {
                      backgroundColor: '#2563eb',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    })
                  }
                  onMouseLeave={e =>
                    Object.assign(e.currentTarget.style, {
                      backgroundColor: '#3b82f6',
                      boxShadow: 'none',
                    })
                  }
                  title="Swap source and target">
                  ⇄ Swap Source & Target
                </button>
              </div>

              {/* Side-by-side comparison */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto 1fr',
                  gap: '16px',
                  marginBottom: '24px',
                  alignItems: 'center',
                }}>
                {/* Source (Left) */}
                <div
                  style={{
                    border: '2px solid #f59e0b',
                    borderRadius: '8px',
                    padding: '16px',
                    backgroundColor: '#fffbeb',
                  }}>
                  <div
                    style={{
                      fontSize: '12px',
                      fontWeight: '600',
                      color: '#92400e',
                      marginBottom: '8px',
                    }}>
                    SOURCE (Will become inactive)
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}>
                      <input
                        type="radio"
                        name="canonical_name"
                        checked={selectedCanonicalName === 'source'}
                        onChange={() => setSelectedCanonicalName('source')}
                      />
                      <span style={{ fontWeight: '500', fontSize: '16px' }}>
                        {sourceName}
                      </span>
                    </label>
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>
                    <div>ID: {sourceId}</div>
                    <div>
                      Created:{' '}
                      {new Date(sourceCreatedDate).toLocaleDateString()}
                    </div>
                  </div>
                </div>

                {/* Arrow */}
                <div style={{ fontSize: '24px', color: '#6b7280' }}>→</div>

                {/* Target (Right) */}
                <div
                  style={{
                    border: '2px solid #10b981',
                    borderRadius: '8px',
                    padding: '16px',
                    backgroundColor: '#ecfdf5',
                  }}>
                  <div
                    style={{
                      fontSize: '12px',
                      fontWeight: '600',
                      color: '#065f46',
                      marginBottom: '8px',
                    }}>
                    TARGET (Canonical)
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}>
                      <input
                        type="radio"
                        name="canonical_name"
                        checked={selectedCanonicalName === 'target'}
                        onChange={() => setSelectedCanonicalName('target')}
                      />
                      <span style={{ fontWeight: '500', fontSize: '16px' }}>
                        {selectedTarget[entityNameField] as string}
                      </span>
                    </label>
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>
                    <div>ID: {selectedTarget[entityIdField] as number}</div>
                    <div>
                      Created:{' '}
                      {new Date(
                        selectedTarget[entityCreatedDateField] as string
                      ).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Preview Data */}
              {previewQuery.status === 'success' && previewData && (
                <div
                  style={{
                    backgroundColor: '#f3f4f6',
                    padding: '16px',
                    borderRadius: '4px',
                    marginBottom: '16px',
                  }}>
                  <h4
                    style={{
                      marginTop: 0,
                      fontSize: '14px',
                      fontWeight: '600',
                    }}>
                    Impact Preview
                  </h4>
                  <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
                    {/* Show facilities if manufacturer domain */}
                    {previewData.source_facility_count !== undefined && (
                      <>
                        <div
                          style={{
                            fontWeight: '600',
                            marginTop: '8px',
                            marginBottom: '4px',
                          }}>
                          Facilities:
                        </div>
                        <div>
                          • Source has{' '}
                          <strong>{previewData.source_facility_count}</strong>{' '}
                          facility/facilities
                        </div>
                        <div>
                          • Target has{' '}
                          <strong>{previewData.target_facility_count}</strong>{' '}
                          facility/facilities
                        </div>
                        <div>
                          • After merge: Total of{' '}
                          <strong>
                            {previewData.total_facilities_after_merge}
                          </strong>{' '}
                          facility/facilities
                        </div>
                      </>
                    )}
                    {/* Show products */}
                    <div
                      style={{
                        fontWeight: '600',
                        marginTop:
                          previewData.source_facility_count !== undefined
                            ? '12px'
                            : '8px',
                        marginBottom: '4px',
                      }}>
                      {previewData.source_facility_count !== undefined
                        ? 'Products:'
                        : ''}
                    </div>
                    <div>
                      • Source has{' '}
                      <strong>
                        {previewData.source_product_count ??
                          previewData.source_related_count}
                      </strong>{' '}
                      product(s)
                    </div>
                    <div>
                      • Target has{' '}
                      <strong>
                        {previewData.target_product_count ??
                          previewData.target_related_count}
                      </strong>{' '}
                      product(s)
                    </div>
                    <div>
                      • After merge: Total of{' '}
                      <strong>
                        {previewData.total_products_after_merge ??
                          previewData.total_related_after_merge}
                      </strong>{' '}
                      product(s)
                    </div>
                    {(previewData.overlapping_related_count > 0 ||
                      (previewData.overlapping_products ?? 0) > 0 ||
                      (previewData.overlapping_facilities ?? 0) > 0) && (
                      <div style={{ color: '#f59e0b', marginTop: '8px' }}>
                        ⚠️ {previewData.overlap_warning}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Domain-specific Conflict Resolution */}
              {previewQuery.status === 'success' &&
                previewData &&
                renderConflicts &&
                renderConflicts(
                  previewData,
                  resolvedFields,
                  handleFieldResolve
                )}

              <div style={{ marginBottom: '16px' }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    marginBottom: '8px',
                    color: '#374151',
                  }}>
                  Merge Reason (Optional)
                </label>
                <textarea
                  value={mergeReason}
                  onChange={e => setMergeReason(e.target.value)}
                  placeholder={`Explain why these ${domainDisplayNamePlural.toLowerCase()} are being merged...`}
                  rows={3}
                  style={{
                    ...modalStyles.floatingTextareaStyle,
                    marginBottom: 0,
                  }}
                />
              </div>
            </>
          )}

          {/* Step 3: Confirm */}
          {step === 'CONFIRM' && selectedTarget && (
            <>
              <h3 style={{ marginTop: 0 }}>Step 3: Confirm Merge</h3>

              <div style={modalStyles.confirmStepWarningContainer}>
                <h4 style={modalStyles.confirmStepWarningTitle}>
                  ⚠️ Warning: This action cannot be undone
                </h4>
                <div style={modalStyles.confirmStepWarningText}>
                  The following changes will be made:
                </div>
              </div>

              <div style={modalStyles.confirmStepChangesContainer}>
                <div style={modalStyles.confirmStepChangesText}>
                  <div>
                    1. <strong>"{sourceName}"</strong> will be marked as
                    inactive
                  </div>
                  <div>
                    2. Canonical name will be:{' '}
                    <strong>
                      "
                      {selectedCanonicalName === 'source'
                        ? sourceName
                        : (selectedTarget[entityNameField] as string)}
                      "
                    </strong>
                  </div>
                  <div>
                    3. All products will be linked to{' '}
                    <strong>
                      "{selectedTarget[entityNameField] as string}"
                    </strong>
                  </div>
                  {previewData && (
                    <div>
                      4. Total products affected:{' '}
                      <strong>{previewData.total_related_after_merge}</strong>
                    </div>
                  )}
                  <div>
                    5. An alias entry will be created for "{sourceName}"
                  </div>
                  <div>6. All changes will be logged in the Audit Log</div>
                </div>
              </div>

              {mergeMutation.status === 'error' && (
                <div
                  style={{
                    marginTop: '16px',
                    padding: '12px',
                    backgroundColor: '#fef2f2',
                    border: '1px solid #ef4444',
                    borderRadius: '4px',
                    color: '#991b1b',
                  }}>
                  Error:{' '}
                  {(mergeMutation.error as Error)?.message || 'Merge failed'}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer - Step 1 */}
        {step === 'SELECT_TARGET' && (
          <div style={modalStyles.modalFooterStyle}>
            <button
              onClick={onClose}
              style={modalStyles.modalCloseButtonStyle}
              onMouseEnter={e =>
                Object.assign(
                  e.currentTarget.style,
                  modalStyles.modalCloseButtonHoverStyle
                )
              }
              onMouseLeave={e =>
                Object.assign(
                  e.currentTarget.style,
                  modalStyles.modalCloseButtonStyle
                )
              }>
              Cancel
            </button>
          </div>
        )}

        {/* Footer - Step 2 */}
        {step === 'RESOLVE_CONFLICTS' && (
          <div style={modalStyles.modalFooterStyle}>
            <button
              onClick={handleBack}
              style={modalStyles.modalCloseButtonStyle}
              onMouseEnter={e =>
                Object.assign(
                  e.currentTarget.style,
                  modalStyles.modalCloseButtonHoverStyle
                )
              }
              onMouseLeave={e =>
                Object.assign(
                  e.currentTarget.style,
                  modalStyles.modalCloseButtonStyle
                )
              }>
              ← Back
            </button>
            <button
              onClick={handleProceedToConfirm}
              style={modalStyles.modalSaveButtonStyle}
              onMouseEnter={e =>
                Object.assign(
                  e.currentTarget.style,
                  modalStyles.modalSaveButtonHoverStyle
                )
              }
              onMouseLeave={e =>
                Object.assign(
                  e.currentTarget.style,
                  modalStyles.modalSaveButtonStyle
                )
              }>
              Next: Review →
            </button>
          </div>
        )}

        {/* Footer - Step 3 */}
        {step === 'CONFIRM' && (
          <div style={modalStyles.modalFooterStyle}>
            <button
              onClick={handleBack}
              style={modalStyles.modalCloseButtonStyle}
              onMouseEnter={e =>
                Object.assign(
                  e.currentTarget.style,
                  modalStyles.modalCloseButtonHoverStyle
                )
              }
              onMouseLeave={e =>
                Object.assign(
                  e.currentTarget.style,
                  modalStyles.modalCloseButtonStyle
                )
              }>
              ← Back
            </button>
            <button
              onClick={handleExecuteMerge}
              disabled={mergeMutation.status === 'loading'}
              style={{
                ...modalStyles.modalSaveButtonStyle,
                backgroundColor:
                  mergeMutation.status === 'loading' ? '#9ca3af' : '#ef4444',
              }}
              onMouseEnter={e =>
                mergeMutation.status !== 'loading' &&
                Object.assign(e.currentTarget.style, {
                  backgroundColor: '#dc2626',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                })
              }
              onMouseLeave={e =>
                Object.assign(e.currentTarget.style, {
                  backgroundColor:
                    mergeMutation.status === 'loading' ? '#9ca3af' : '#ef4444',
                  boxShadow: 'none',
                })
              }>
              {mergeMutation.status === 'loading'
                ? 'Merging...'
                : 'Confirm Merge'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default GenericMergeModal;
